/**
 * Edit the setup: a workspace that already exists, changed in the studio and
 * built again (step 9.16 of docs/plans/onboarding-harness/phase-09.md). This
 * file holds the shapes the server builds with.
 *
 *   SetupNow         the workspace as it stands: what an edit session is
 *                    seeded from (`draftFromWorkspace`) and what a build of it
 *                    is checked against.
 *   SetupChange      one change Build again makes: a record's fields changed,
 *                    a record added (with the list it needs), or a record left
 *                    out. The server applies the whole set in one transaction
 *                    or not at all (`applySetupChanges`).
 *
 * Nothing here writes a row; every rule about what may change is the
 * server's, and it answers with every problem at once.
 */
import type { RecordBlankCounts, RecordIconId, VisibleRecordKind } from './api';
import type { AccountChoice } from './ledger/account-choice';
import type { LedgerAccountType } from './ledger/patterns';
import type { StudioTile } from './setup-draft/draft';
import type { SpecApprovalChain } from './spec/approval-chain';
import type { SpecChargePlan } from './spec/charge-plan';
import type { SpecAccountsList, SpecPostingRule, SpecSeedAccount } from './spec/posting';
import type { SpecTaskRule } from './task-rules';

/* ------------------------------------------------------------ the workspace */

/** Which way a record moves money, as the outline words it; null on a list. */
export type SetupNowDirection = 'in' | 'out' | 'both' | 'count';

export type SetupNowField = {
  readonly id: string;
  readonly label: string;
  readonly type: string;
  /** The record a reference picks from. */
  readonly targetRecordId: string | null;
  readonly required: boolean;
};

export type SetupNowRecord = {
  readonly id: string;
  readonly name: string;
  readonly kind: VisibleRecordKind;
  readonly direction: SetupNowDirection | null;
  readonly icon: RecordIconId | null;
  /** The version a change to its fields is made on; a newer one refuses the change. */
  readonly fieldSetVer: number;
  /** Live entries (rows, on a list): what leaving it out keeps. */
  readonly entries: number;
  /** Its live fields, in form order. */
  readonly fields: readonly SetupNowField[];
};

/** The workspace as it stands, as `loadSetupNow` reads it. */
export type SetupNow = {
  readonly tenantId: string;
  readonly name: string;
  readonly currency: string;
  readonly records: readonly SetupNowRecord[];
};

/* ------------------------------------------------------------- the changes */

/** The kinds of field a change may add. A worked-out field is the record's own editor's. */
export const SETUP_FIELD_TYPES = [
  'text',
  'long_text',
  'number',
  'money',
  'quantity',
  'date',
  'datetime',
  'boolean',
  'select',
  'reference',
  'phone',
] as const;

export type SetupFieldType = (typeof SETUP_FIELD_TYPES)[number];

/**
 * A field a change adds. A reference picks from a record that is there
 * (`targetRecordId`) or from one this same set adds (`targetRecordKey`, the
 * add's `key`); never both.
 */
export type SetupFieldAdd = {
  readonly key: string;
  readonly label: string;
  readonly type: SetupFieldType;
  readonly required?: boolean;
  /** The choices of a select, in the person's words. */
  readonly options?: readonly string[];
  /** Each choice's key, in the order of `options`, when the draft names them: the keys a rule reads (a charge plan's `cycles`). */
  readonly optionKeys?: readonly string[];
  readonly targetRecordId?: string;
  readonly targetRecordKey?: string;
  /** A pick of the Accounts list only (L16): which accounts it offers, and what a typed category is made as. */
  readonly accounts?: AccountChoice;
};

/** A row Build again adds to the Accounts list (step V2.2 of docs/plans/ledger.md): its name, its type, and what the ledger keeps of it. */
export type SetupAccountRowAdd = { readonly name: string; readonly type: LedgerAccountType; readonly account?: SpecSeedAccount };

/**
 * How a record counts money, which of its entries wait for approval, and the
 * tasks it raises, as Build again writes them (step V2.2 of
 * docs/plans/ledger.md): each part that differs from the base, as the draft
 * holds it now. A part left out is not changed.
 */
export type SetupMoneyChange = {
  readonly kind: 'money';
  /** A record that is there, or one this same set adds. */
  readonly record: SetupRecordRef;
  /** The draft record's key, so the field keys of its parts resolve. */
  readonly recordKey: string;
  /**
   * Its money rule now. A changed rule counts new saves only: entries already
   * saved keep what they counted. Null takes the rule off, which is refused
   * while an entry it counted stands (Counts in money goes off instead).
   */
  readonly posting?: SpecPostingRule | null;
  /** True when the record had a money rule when Edit opened: the rule is changed, not new. */
  readonly ruleHeld?: boolean;
  /** A new list that is the Accounts list: its Type field (L15). */
  readonly accounts?: SpecAccountsList;
  /** Rows added to the Accounts list, each with its account, in one transaction (L15). */
  readonly accountRows?: readonly SetupAccountRowAdd[];
  /** A list billed on a schedule (L23): its Charges record is made with it. */
  readonly chargePlan?: SpecChargePlan;
  /** Which entries wait for approval and who decides each step (L55); null takes the chain off. */
  readonly approvalChain?: SpecApprovalChain | null;
  /** Task rules added to the record, after the ones it holds. */
  readonly tasks?: readonly SpecTaskRule[];
};

/** A field renamed. */
export type SetupFieldRename = { readonly fieldId: string; readonly label: string };

/**
 * A built field's own settings changed (step 9.16 of
 * docs/plans/onboarding-harness/phase-09.md): whether it must be filled, and
 * new choices on a select. Entries already kept are untouched.
 */
export type SetupFieldPatch = { readonly fieldId: string; readonly required?: boolean; readonly addOptions?: readonly string[] };

/** A record that is there (`recordId`) or one this same set adds (`recordKey`, the add's `key`). */
export type SetupRecordRef = { readonly recordId: string } | { readonly recordKey: string };

export type SetupChange =
  | {
      /** A record's own fields: added, taken off (archived, never deleted), renamed, patched. */
      readonly kind: 'change';
      readonly recordId: string;
      readonly fieldSetVer: number;
      readonly add?: readonly SetupFieldAdd[];
      readonly archive?: readonly string[];
      readonly rename?: readonly SetupFieldRename[];
      readonly patch?: readonly SetupFieldPatch[];
    }
  | {
      /** A record or a list that is not there yet. `key` names it to a reference in this set. */
      readonly kind: 'add';
      readonly key: string;
      readonly recordKind: VisibleRecordKind;
      readonly name: string;
      /** What each entry counts, on a record; absent on a list. */
      readonly counts?: RecordBlankCounts;
      readonly fields: readonly SetupFieldAdd[];
      /** A new list's first names (at most `SETUP_CHANGE_LIMITS.rows`), written as its first rows. */
      readonly rows?: readonly string[];
    }
  | {
      /** A record left out: archived with every entry it holds, never deleted. */
      readonly kind: 'leave_out';
      readonly recordId: string;
    };

/**
 * The changes Build again writes (9.16): every `SetupChange`, and the kinds
 * Edit the setup in the studio adds. The old changes route and its readers
 * keep to `SetupChange`; only an edit session's build writes these.
 */
export type SetupEditChange =
  | SetupChange
  | {
      /** A built record renamed: its entries stay; only the name changes. */
      readonly kind: 'rename_record';
      readonly recordId: string;
      readonly name: string;
    }
  | {
      /** A new card on top of a record, as the draft holds it (by field keys). */
      readonly kind: 'add_card';
      readonly record: SetupRecordRef;
      /** The draft record's key, so its field keys resolve. */
      readonly recordKey: string;
      readonly tile: StudioTile;
    }
  | {
      /** A built card taken off its record: `cardId` is `<summary view id>#<place>` (`draftFromWorkspace`'s `built`). */
      readonly kind: 'remove_card';
      readonly recordId: string;
      readonly cardId: string;
    }
  | SetupMoneyChange;

/** One record the build touched, and how. */
export type SetupChangeMade = {
  readonly recordId: string;
  readonly name: string;
  readonly kind: VisibleRecordKind;
  readonly made: 'changed' | 'added' | 'left_out' | 'renamed';
};

/**
 * One reason the set was not built. `change` is its place in the set, and
 * `recordId`/`recordName` the record it is about (a record this set adds has
 * no id yet), so the outline marks it To fix.
 */
export type SetupChangeProblem = {
  readonly change: number;
  readonly code: string;
  readonly message: string;
  readonly recordId: string | null;
  readonly recordName: string;
};

/** The ceilings, generous for what a person says in one conversation. */
export const SETUP_CHANGE_LIMITS = {
  changes: 12,
  fields: 20,
  name: 60,
  label: 60,
  options: 30,
  key: 40,
  /** The first names a new list is given in Edit the setup (9.16), as a draft's seed rows. */
  rows: 24,
} as const;

/**
 * How many changes the person is asked to build. A list added only because a
 * record added beside it picks from it is part of that record's change
 * ("Home deliveries: new, with a new list, Riders"), so it is not counted on
 * its own.
 */
export const setupChangeCount = (changes: readonly SetupChange[]): number => {
  const pickedByAdd = new Set(
    changes.flatMap((change) =>
      change.kind === 'add' ? change.fields.flatMap((field) => (field.targetRecordKey === undefined ? [] : [field.targetRecordKey])) : [],
    ),
  );
  return changes.filter((change) => !(change.kind === 'add' && change.recordKind === 'directory' && pickedByAdd.has(change.key))).length;
};
